import {
	configureChains,
	switchNetwork,
	watchNetwork,
	watchAccount,
	createConfig
} from '@wagmi/core';
import { optimism, type Chain } from '@wagmi/core/chains';
import { alchemyProvider } from '@wagmi/core/providers/alchemy';
import { infuraProvider } from '@wagmi/core/providers/infura';

import {
	chain,
	overrideWalletAddress,
	scWalletAccountResolver,
	walletAddress,
	walletConnected,
	walletLoaded
} from './store';
import { get } from 'svelte/store';
import { checkTosAndSCWallet } from '$lib/util/common';
import { PUBLIC_ALCHEMY_API_KEY, PUBLIC_INFURA_API_KEY } from '$env/static/public';
import { setupMarketDataRefresh } from '$lib/data/refreshData';
import { web3Modal } from './init.store';
import { isCyberWallet } from '@cyberlab/cyber-app-sdk';

const PROJECT_ID = '5dd0d183716238171912e9562fae2c9c';

const alchemy_api_key = PUBLIC_ALCHEMY_API_KEY || 'lb8tcInB4sWR4uP8zA7Prvx2pTm6PtSZ';
const infura_api_key = PUBLIC_INFURA_API_KEY || '12287e924f854b17a6b9f930b7797b72';

export const supportedChains: Array<Chain> = [optimism];

export async function initWallet() {
	let createWeb3Modal: any;
	let defaultWagmiConfig: any;
	let InjectedConnector: any;
	let WalletConnectConnector: any;
	let MetaMaskConnector: any;
	let CoinbaseWalletConnector: any;
	let CyberWalletConnector: any;

	if (typeof window !== 'undefined') {
		const { createWeb3Modal: c, defaultWagmiConfig: d } = await import('@web3modal/wagmi');
		const { CyberWalletConnector: cw } = await import('@cyberlab/cyber-app-sdk');
		const { InjectedConnector: i } = await import('@wagmi/connectors/injected');
		const { WalletConnectConnector: w } = await import('@wagmi/connectors/walletConnect');
		const { MetaMaskConnector: m } = await import('@wagmi/connectors/metaMask');
		const { CoinbaseWalletConnector: cb } = await import('@wagmi/connectors/coinbaseWallet');

		createWeb3Modal = c;
		defaultWagmiConfig = d;
		InjectedConnector = i;
		WalletConnectConnector = w;
		MetaMaskConnector = m;
		CoinbaseWalletConnector = cb;
		CyberWalletConnector = cw;
	}

	const { chains, publicClient, webSocketPublicClient } = configureChains(
		supportedChains,
		[alchemyProvider({ apiKey: alchemy_api_key }),infuraProvider({ apiKey: infura_api_key })]
	);

	const metadata = {
		name: 'Polynomial Trade',
		description: 'Trade decentralised perps with low fees and deep liquidity',
		url: 'https://trade.polynomial.fi',
		icons: ['https://trade.polynomial.fi/Logo.webp']
	};

	let connectors = [
		new WalletConnectConnector({
			chains,
			options: { projectId: PROJECT_ID, showQrModal: false, metadata }
		}),
		new InjectedConnector({ chains, options: { shimDisconnect: true } }),
		new CoinbaseWalletConnector({ chains, options: { appName: metadata?.name ?? 'Unknown' } })
	];

	if (isCyberWallet()) {
		const cyberWalletConnector = new CyberWalletConnector({
			chains: supportedChains,
			options: {
				appId: '1d39aa52-3c0a-404b-89a8-9f73df0b0483', // required
				name: 'Polynomial', // required
				icon: 'https://uploads-ssl.webflow.com/61dde1260c66047f40c11259/657c959eac6f5653e41150e9_Polynomial%20Logo.png'
			}
		});
		connectors.push(cyberWalletConnector);
	}

	let wagmiConfig = createConfig({
		autoConnect: true,
		connectors,
		publicClient,
		webSocketPublicClient
	});

	const themeVariables = {
		'--w3m-border-radius-master': '4px',
		'--w3m-color-mix': '#000000',
		'--w3m-color-mix-strength': 40,
		'--w3m-accent': '#1D1D1D'
	};

	const modal = createWeb3Modal({
		wagmiConfig,
		projectId: PROJECT_ID,
		chains: supportedChains,
		themeVariables,
		themeMode: 'dark',
		excludeWalletIds: ['19177a98252e07ddfc9af2083ba8e07ef627cb6103467ffebb3f8f4205fd7927']
	});
	web3Modal.set(modal);
}

// Wagmi Core Client

export function trackCurrentAccount() {
	watchAccount(async (account) => {
		walletConnected.set(account.isConnected);
		walletLoaded.set(true);

		setupMarketDataRefresh();
		try {
			if (get(overrideWalletAddress) && get(overrideWalletAddress) !== '') {
				walletAddress.set(get(overrideWalletAddress));
			} else if (account?.address) {
				walletAddress.set(account.address);
			}
			await checkTosAndSCWallet(get(scWalletAccountResolver));
		} catch (e) {
			console.log(e);
		}
	});

	watchNetwork((network) => {
		chain.set(network.chain);
	});
}

export const switchToOptimism = function () {
	switchNetwork({ chainId: optimism.id });
};

export const switchToNetworkId = (id: number) => {
	switchNetwork({ chainId: id });
};

export const openWeb3Modal = function () {
	get(web3Modal)?.open();
};
